// frontend/src/lib/remediationSnapshot.js
import { NET_METHODS, computeExposure } from "./remediationMath";

/**
 * Utilities expected by RemediationHub / FindingCard / export
 *
 * Key responsibilities:
 * - Safe JSON read helpers
 * - Stable IDs for evidence/upload rows
 * - Build remediation snapshot for export (JSON)
 * - Snapshot-scoped remediation map storage (MVP carry-forward foundation)
 *
 * NOTE: Evidence Upload is currently "upload-ready metadata" only (not real file storage).
 *
 * © 2026 Advanced Advisory Services, LLC. All Rights Reserved.
 */

/* ---------------------------------------------
 * Basic helpers
 * --------------------------------------------- */

export function isLikelyUrl(s) {
  if (!s || typeof s !== "string") return false;
  const v = s.trim();
  return /^https?:\/\/\S+/i.test(v) || /^www\.\S+/i.test(v);
}

export function cryptoRandomId(prefix = "id") {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
      return `${prefix}_${crypto.randomUUID()}`;
    }
  } catch {
    // ignore
  }
  return `${prefix}_${Math.random().toString(16).slice(2)}_${Date.now()}`;
}

export function safeReadSnapshot(raw, fallback = null) {
  try {
    if (!raw) return fallback;
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function safeObj(x) {
  return x && typeof x === "object" && !Array.isArray(x) ? x : {};
}

/* ---------------------------------------------
 * Remediation map storage (legacy + snapshot-scoped)
 * --------------------------------------------- */

// Legacy key (back-compat)
export const LEGACY_REMEDIATION_KEY = "aifine_remediation_map_v1";

// Snapshot-scoped keys (new)
export const REMEDIATION_MAP_PREFIX = "aifine_remediation_map__";
export const REMEDIATION_MAP_SUFFIX = "__v1";

export function remediationMapKeyForSnapshot(snapshotId) {
  const sid = String(snapshotId || "").trim();
  if (!sid) return LEGACY_REMEDIATION_KEY; // fallback
  return `${REMEDIATION_MAP_PREFIX}${sid}${REMEDIATION_MAP_SUFFIX}`;
}

/**
 * Read remediation map.
 * Order:
 *  1) snapshot-scoped key (if snapshotId exists)
 *  2) legacy key
 *
 * IMPORTANT: localStorage reads can throw in restricted environments -> fail safe.
 */
export function readRemediationMap({ snapshotId } = {}) {
  const sid = String(snapshotId || "").trim();
  const scopedKey = remediationMapKeyForSnapshot(sid);

  // Try snapshot-scoped first (if we have a snapshot id)
  if (sid) {
    try {
      const raw = localStorage.getItem(scopedKey);
      const scoped = safeReadSnapshot(raw, null);
      if (scoped && typeof scoped === "object" && !Array.isArray(scoped)) return scoped;
    } catch {
      // ignore; fall through to legacy
    }
  }

  // Fallback to legacy
  try {
    const rawLegacy = localStorage.getItem(LEGACY_REMEDIATION_KEY);
    const legacy = safeReadSnapshot(rawLegacy, {});
    if (legacy && typeof legacy === "object" && !Array.isArray(legacy)) return legacy;
  } catch {
    // ignore
  }

  return {};
}

/**
 * Write remediation map.
 * Behavior:
 * - If snapshotId provided -> write snapshot-scoped key
 * - Always also write legacy key (to keep older flows working)
 */
export function writeRemediationMap({ snapshotId, map } = {}) {
  const sid = String(snapshotId || "").trim();
  const next = map && typeof map === "object" && !Array.isArray(map) ? map : {};

  try {
    // Snapshot-scoped write (preferred)
    if (sid) {
      localStorage.setItem(remediationMapKeyForSnapshot(sid), JSON.stringify(next));
    }

    // Legacy write (back-compat)
    localStorage.setItem(LEGACY_REMEDIATION_KEY, JSON.stringify(next));
  } catch {
    // Non-fatal: storage may be full / blocked
  }

  return next;
}

/* ---------------------------------------------
 * Snapshot schema + validation
 * --------------------------------------------- */

export const SNAPSHOT_SCHEMA = {
  version: "1.0",
  requiredTopLevelKeys: ["version", "created_at_iso", "method", "payload"],
};

function isAllowedMethod(method) {
  const m = String(method || "");
  // STRICT is the current enforced method; keep compat if remediationMath still exposes MATURITY
  if (NET_METHODS?.STRICT && m === NET_METHODS.STRICT) return true;
  if (NET_METHODS?.MATURITY && m === NET_METHODS.MATURITY) return true;
  return false;
}

export function validateSnapshot(snapshot) {
  const errors = [];
  if (!snapshot || typeof snapshot !== "object") {
    return { ok: false, errors: ["Snapshot is not an object"] };
  }

  for (const k of SNAPSHOT_SCHEMA.requiredTopLevelKeys) {
    if (!(k in snapshot)) errors.push(`Missing key: ${k}`);
  }

  if (snapshot.version !== "1.0") errors.push("Unsupported snapshot version");

  if (!isAllowedMethod(snapshot.method)) {
    errors.push("Invalid method");
  }

  // Optional: remediation_map
  if (
    "remediation_map" in snapshot &&
    snapshot.remediation_map !== null &&
    (typeof snapshot.remediation_map !== "object" || Array.isArray(snapshot.remediation_map))
  ) {
    errors.push("Invalid remediation_map");
  }

  // Optional: config
  if ("config" in snapshot && snapshot.config !== null && typeof snapshot.config !== "object") {
    errors.push("Invalid config");
  }

  // Optional: findings
  if ("findings" in snapshot && snapshot.findings !== null && !Array.isArray(snapshot.findings)) {
    errors.push("Invalid findings");
  }

  return { ok: errors.length === 0, errors };
}

/**
 * Apply snapshot to local state (lightweight).
 * Returns { ok, errors, state: { remediationMap?, netMethod } }
 */
export function applySnapshotToState(snapshot) {
  const v = validateSnapshot(snapshot);
  if (!v.ok) return { ok: false, errors: v.errors, state: null };

  const state = {
    netMethod: snapshot.method,
  };

  if (snapshot.remediation_map && typeof snapshot.remediation_map === "object") {
    state.remediationMap = snapshot.remediation_map;
  }

  return { ok: true, errors: [], state };
}

/* ---------------------------------------------
 * Remediation snapshot builder (for export)
 * --------------------------------------------- */

/**
 * Extract STRICT baseline gross exposure:
 * v3 preferred:
 * - audit.risk_exposure.headline.combined_structured_worst_case_native
 * - fallback: first bucket in audit.risk_exposure.combined_by_currency[<code>].combined_structured_worst_case_native
 *
 * legacy fallback:
 * - audit.summary.gross_exposure_ceiling_usd
 * - audit.summary.gross_exposure_ceiling
 * - audit.summary.gross
 * - audit.gross
 */
function extractGrossBaselineForExport(auditResults) {
  const audit = safeObj(auditResults);
  const rx = safeObj(audit?.risk_exposure);

  const headline = safeObj(rx?.headline);
  const headlineGross = Number(headline?.combined_structured_worst_case_native);
  if (Number.isFinite(headlineGross) && headlineGross >= 0) return headlineGross;

  const byCcy = safeObj(rx?.combined_by_currency);
  const codes = Object.keys(byCcy || {}).filter(Boolean);
  if (codes.length) {
    const b = safeObj(byCcy[codes[0]]);
    const g2 = Number(b?.combined_structured_worst_case_native);
    if (Number.isFinite(g2) && g2 >= 0) return g2;
  }

  const s = safeObj(audit?.summary);
  const legacy =
    Number(s?.gross_exposure_ceiling_usd) ||
    Number(s?.gross_exposure_ceiling) ||
    Number(s?.gross) ||
    Number(audit?.gross) ||
    0;

  return Number.isFinite(legacy) ? legacy : 0;
}

/**
 * Core snapshot builder.
 * Additive fields (backward compatible):
 *  - config: { clientName, jurisdictions[] }
 *  - findings: minimal list for rendering
 *  - remediation_map: restore remediation UI state on import
 */
export function buildRemediationSnapshot({
  auditResults,
  findings,
  remediationMap,
  netMethod = NET_METHODS.STRICT,
}) {
  const audit = safeObj(auditResults);

  // IMPORTANT: align exports with RemediationHub STRICT baseline (v3 risk_exposure when present)
  const gross = extractGrossBaselineForExport(audit);

  const exposure = computeExposure({
    gross,
    findings,
    remediationMap,
    method: netMethod,
  });

  // Pull config if available (canonical payload has it)
  const cfg = audit?.config || null;
  const config = cfg
    ? {
        clientName: cfg.clientName || null,
        jurisdictions: Array.isArray(cfg.jurisdictions)
          ? cfg.jurisdictions
          : cfg.jurisdiction
          ? [cfg.jurisdiction]
          : [],
      }
    : null;

  // Minimal findings for export (evidence/status live in remediation_map)
  const safeFindings = Array.isArray(findings)
    ? findings.map((f) => ({
        id: f?.id ?? null,
        kind: f?.kind ?? null,
        category: f?.category ?? null,
        title: f?.title ?? null,
        text: f?.text ?? null,
        gravity: f?.gravity ?? null,
      }))
    : [];

  return {
    version: "1.0",
    created_at_iso: new Date().toISOString(),
    method: netMethod,

    payload: {
      gross_exposure: exposure.gross,
      net_exposure: exposure.net,
      credit_applied_pct: Math.round((Number(exposure.creditAppliedPct || 0) || 0) * 100),
      weighted_credit_amount: Math.round(Number(exposure.weightedCreditAmount || 0) || 0),
      sum_gravity: exposure.sumGravity,
      findings_count: safeFindings.length,
    },

    // Additive (do not break older imports)
    config,
    findings: safeFindings,
    remediation_map:
      remediationMap && typeof remediationMap === "object" && !Array.isArray(remediationMap)
        ? remediationMap
        : {},
    audit_summary: audit?.summary || null,

    // Helpful for future import/debug (optional, backward compatible)
    snapshot_id: audit?.snapshot_id ?? null,
    business_id: audit?.business_id ?? audit?.config?.business_id ?? audit?.meta?.business_id ?? null,
  };
}

// Back-compat alias used by older imports
export function buildSnapshot(args) {
  return buildRemediationSnapshot({
    auditResults: args?.auditResults ?? args?.results ?? args?.audit ?? null,
    findings: args?.findings ?? [],
    remediationMap: args?.remediationMap ?? args?.remediation ?? {},
    netMethod: args?.netMethod ?? args?.method ?? NET_METHODS.STRICT,
  });
}

/* ---------------------------------------------
 * JSON download helper
 * --------------------------------------------- */

export function downloadJson(filenameBase, obj) {
  const name = `${filenameBase || "remediation_snapshot"}_${new Date()
    .toISOString()
    .replace(/[:.]/g, "-")}.json`;

  const json = JSON.stringify(obj, null, 2);
  const blob = new Blob([json], { type: "application/json" });
  const url = URL.createObjectURL(blob);

  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();

  URL.revokeObjectURL(url);
}

export default {
  SNAPSHOT_SCHEMA,
  validateSnapshot,
  applySnapshotToState,
  buildRemediationSnapshot,
  buildSnapshot,
  cryptoRandomId,
  safeReadSnapshot,
  isLikelyUrl,
  downloadJson,
  LEGACY_REMEDIATION_KEY,
  REMEDIATION_MAP_PREFIX,
  REMEDIATION_MAP_SUFFIX,
  remediationMapKeyForSnapshot,
  readRemediationMap,
  writeRemediationMap,
};